import * as graphics from "@client/app/graphics";
import {MenuIcon} from "@client/components/Navigation";
import {APP_STORE_URL} from "@client/constants";
import * as dates from "@client/dates";
import * as auth from "@client/features/auth";
import * as healthkit from "@client/features/healthkit";
import * as orm from "@client/features/orm";
import * as hardware from "@client/hardware";
import * as router from "@client/router";
import type {JournalImplementation} from "@client/scenes/Journal";
import {AnimatedButton} from "@client/scenes/PlanOnboarding";
import * as analytics from "@client/shared/analytics";
import {useAppDispatch, useAppSelector} from "@client/store";
import {resizeImage} from "@client/utils";
import {
    ArrowRightIcon,
    CalendarDaysIcon,
    CameraIcon,
    HomeIcon,
    SunIcon,
} from "@heroicons/react/24/outline";
import NumberFlow from "@number-flow/react";
import {classNames, server} from "@reactivated";
import {produce} from "immer";
import {AnimatePresence, motion} from "motion/react";
// Walking club (issue #1800), design per walk-club-flow-complete-2.
// Two scenes: the join gate at /club/<code>/ and the member board at /club/.
// Off production, the gate wizard screens stay reachable with fake data via
// ?step=email|email_exists|profile|connect|denied; ?tab=week opens the board
// on the week tab. The board itself has no demo step — a seeded preview
// renders the real thing.
import React from "react";
import {match} from "ts-pattern";

const IND = "#5546e5";
const CORAL = "#f8765b";
// Neutral chrome colors ride CSS variables so dark mode flips the SVG and
// inline-style consumers in one place (CLUB_THEME below); the fallbacks keep
// the gate wizard — outside CLUB_THEME — on its light values.
const TRACK = "var(--club-track, #eceae8)";
const DASH = "var(--club-dash, #d6d3d1)";
const BADGE = "var(--club-badge, #e7e5e4)";

// Applied to the board's two roots (shell and bottom bar) so every neutral
// below them follows the scheme. The foreground rides along: the bottom bar
// is a SEPARATE root — SimpleScene renders bottomSection outside the shell —
// so a text colour declared on the shell alone never reaches the docked copy
// of your row, which is how it drew black on dark.
const CLUB_THEME = classNames(
    "[--club-track:#eceae8] [--club-dash:#d6d3d1] [--club-badge:#e7e5e4]",
    "dark:[--club-track:#334155] dark:[--club-dash:#475569] dark:[--club-badge:#475569]",
    "dark:text-slate-100",
);

const IMAGE_DELIVERY = "https://imagedelivery.net/ELiLiOfYeVi79Put2yGLDg";

const AVATAR_COLORS = [
    "#3b2fd4",
    "#5546e5",
    "#4c3ee0",
    "#7f74ef",
    "#6d5af0",
    "#8a5be0",
    "#9187f2",
    "#a9a1f4",
    "#b7b0f6",
];

const avatarColor = (name: string) =>
    AVATAR_COLORS[
        [...name].reduce((sum, char) => sum + char.charCodeAt(0), 0) %
            AVATAR_COLORS.length
    ];

interface BoardMember {
    uuid: string;
    name: string;
    imageUrl: string | null;
    color: string;
    you: boolean;
    live: boolean;
    // The word for this member's rest week, or null when competing. The row
    // still shows real steps; it is out of the running — no rank, no day
    // win, and the colour drains out of it. One field, not a flag plus a
    // label, so the two can never disagree.
    resting: string | null;
    // Thousands of steps, ready for StepsK.
    hourly: number[];
    daily: (number | null)[];
    todayTotal: number;
    weekTotal: number;
}

interface BoardData {
    goal: number;
    lastCompleteWeekday: number;
    weekStart: DateValue;
    weeksAvailable: number;
    members: BoardMember[];
}

const fromServer = (board: server.journal.clubs.WalkingClubBoard): BoardData => ({
    goal: board.steps_goal / 1000,
    lastCompleteWeekday: board.last_complete_weekday,
    weekStart: board.week_start,
    weeksAvailable: board.weeks_available,
    members: board.members.map((member) => ({
        uuid: member.member_uuid,
        name: member.display_name,
        imageUrl:
            member.image_uid != null
                ? `${IMAGE_DELIVERY}/${member.image_uid}/public`
                : null,
        color: member.is_you ? CORAL : avatarColor(member.display_name),
        you: member.is_you,
        live: member.is_live,
        resting: member.resting,
        // Two-hour display buckets keep the sparkline density readable.
        hourly: Array.from(
            {length: 12},
            (_, index) =>
                (member.today_buckets[index * 2] +
                    member.today_buckets[index * 2 + 1]) /
                1000,
        ),
        daily: member.week_daily.map((value) => (value == null ? null : value / 1000)),
        todayTotal: member.today_total / 1000,
        weekTotal: member.week_total / 1000,
    })),
});

// A sync writes steps into the store immediately but POSTs them
// fire-and-forget, so re-reading the board on resume can beat the server's
// own copy. Your row trusts whichever number is higher; everyone else's can
// only come from the server. Once steps upload natively (#1807) the server
// is the fresh source and this quietly stops winning.
const useYourLocalSteps = () => {
    const todayKey = dates.format(new Date(), "yyyy-MM-dd");
    return useAppSelector((state) => state.orm.metrics[todayKey]?.STEPS?.value ?? null);
};

const withYourLocalSteps = (data: BoardData, localSteps: number | null): BoardData =>
    localSteps == null
        ? data
        : produce(data, (draft) => {
              const you = draft.members.find((member) => member.you);
              if (you == null) {
                  return;
              }
              const local = localSteps / 1000;
              if (local <= you.todayTotal) {
                  return;
              }
              // Today feeds the week: patching only todayTotal left the week
              // tab reading stale after a resume sync (#1833). This only ever
              // runs on the CURRENT board, where lastCompleteWeekday IS
              // today's column.
              const previousToday = you.daily[draft.lastCompleteWeekday] ?? 0;
              you.todayTotal = local;
              you.daily[draft.lastCompleteWeekday] = local;
              you.weekTotal = you.weekTotal - previousToday + local;
          });

// The validated prototype dataset — the gate backdrop everywhere, and the
// whole board when previewing ?step screens off production.
const DEMO_ROWS: [string, number[], number[], number][] = [
    [
        "Ben",
        [0.3, 4.8, 3.1, 0.4, 0.5, 0.6, 1.2, 2.4, 0.9, 0],
        [12, 6, 11, 5.8, 14.2],
        12,
    ],
    [
        "Kat",
        [0, 0.4, 1.1, 0.8, 0.7, 1.5, 4.6, 3.2, 0.5, 0.3],
        [11, 10, 4, 13.9, 13.1],
        47,
    ],
    [
        "Priya",
        [0, 1.2, 2.8, 2.4, 1.1, 0.8, 1.5, 1.4, 0.9, 0.5],
        [8, 10.4, 9, 8, 12.6],
        25,
    ],
    ["Maria", [0, 0.6, 0.9, 2.8, 3.1, 1.2, 0.9, 1.4, 0.5, 0], [5, 6, 6, 5.6, 11.4], 45],
    ["Tom", [0, 0.5, 0.8, 1.2, 1.5, 1.8, 1.6, 1.4, 1, 0.4], [8, 7, 9, 6.6, 10.2], 13],
    ["Leo", [0, 0.3, 0.7, 0.9, 1.6, 2.1, 1.4, 1.2, 0.8, 0], [7, 6, 4, 10, 9], 33],
    ["Nadia", [0, 0, 0.6, 0.9, 1.2, 1.5, 1.8, 1.4, 0.7, 0.3], [6, 8, 9, 6.6, 8.4], 24],
    ["Ana", [0, 0.2, 0.5, 0.6, 0.8, 1.1, 0.9, 0.7, 0.5, 0], [8, 9, 7, 4.7, 5.3], 44],
    ["Omar", [0, 0, 0.4, 0.6, 0.9, 1.1, 0.8, 0.5, 0.3, 0], [5, 6, 7, 7.4, 4.6], 59],
    [
        "Silvio",
        [0.2, 1.6, 0.9, 0.4, 0.3, 0.2, 0.1, 0, 0, 0],
        [12.1, 5, 11.2, 11, 3.7],
        68,
    ],
    ["Jae", [0, 0, 0.3, 0.4, 0.6, 0.7, 0.5, 0.3, 0.1, 0], [4, 5, 4, 6.1, 2.9], 61],
];

const DEMO_BOARD: BoardData = {
    goal: 10,
    lastCompleteWeekday: 4,
    weekStart: "2026-08-17",
    weeksAvailable: 0,
    members: DEMO_ROWS.map(([name, hourly, daily, avatarId]) => ({
        uuid: name,
        name,
        imageUrl: `https://i.pravatar.cc/96?img=${avatarId}`,
        color: name === "Silvio" ? CORAL : avatarColor(name),
        you: name === "Silvio",
        live: hourly[hourly.length - 1] > 0,
        resting: null,
        hourly,
        daily: [...daily, null, null].slice(0, 7),
        todayTotal: Math.round(hourly.reduce((sum, value) => sum + value, 0) * 10) / 10,
        weekTotal: Math.round(daily.reduce((sum, value) => sum + value, 0)),
    })),
};

const byToday = (board: BoardData) =>
    [...board.members].sort((a, b) => b.todayTotal - a.todayTotal);
const byWeek = (board: BoardData) =>
    [...board.members].sort(
        (a, b) => b.weekTotal - a.weekTotal || b.todayTotal - a.todayTotal,
    );

const maxHourly = (board: BoardData) =>
    Math.max(0.1, ...board.members.flatMap((member) => member.hourly));
const maxDaily = (board: BoardData) =>
    Math.max(
        0.1,
        ...board.members.flatMap((member) =>
            member.daily.filter((value) => value != null),
        ),
    );

// Boxed users' whole app IS the club (the WALKING_CLUB flag); anonymous
// viewers count as boxed too. The single source for a predicate that
// otherwise drifts into inverted copies.
export const isWalkingClubBoxed = (profile: server.core.rpc.Profile.output | null) =>
    profile == null ||
    profile.user.casted_dismissed_announcements.includes("WALKING_CLUB");

const truncK = (value: number) => Math.floor(value * 10) / 10;

const formatK = (value: number) => `${truncK(value)}k`;

// Steps land in jumps — a resume sync can credit hours of walking at once —
// so the totals roll to the new figure rather than swapping in place, the
// same treatment the journal's steps card gets. Precision matches what these
// numbers always showed: a decimal below 10k, whole thousands above it, and
// week totals whole throughout.
// Always truncate, never round: 14,900 steps reads as 14.9k, and a total never
// claims ground the walker hasn't covered — the last hundred metres are the
// motivating part. One decimal at every magnitude, week totals included.
const StepsK = ({value}: {value: number}) => (
    <NumberFlow
        value={truncK(value)}
        format={{maximumFractionDigits: 1}}
        suffix="k"
        className="missing-tailwind-support-club-steps"
    />
);

const MEDAL_COLORS = ["#e0a63b", "#a8a29e", "#c98a5b"];

const WalkBadge = () => (
    <span
        className="absolute bottom-[-3px] left-[-3px] z-10 flex size-4 animate-pulse items-center justify-center rounded-full border-2 border-white motion-reduce:animate-none"
        style={{backgroundColor: IND}}
    >
        <svg
            viewBox="0 0 24 24"
            width="9"
            height="9"
            fill="none"
            stroke="#fff"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <circle cx="13" cy="4.5" r="1.8" fill="#fff" stroke="none" />
            <path d="M12.5 8l-2.5 5 2.5 3 1 5M10 13l-2.5 5M12.5 8l3 2 2.5 1M12.5 8 10 9.5 8.5 12" />
        </svg>
    </span>
);

const Avatar = ({
    member,
    rank = null,
    size = 38,
    showLive = false,
}: {
    member: Pick<BoardMember, "name" | "imageUrl" | "color" | "you" | "live">;
    rank?: number | null;
    size?: number;
    showLive?: boolean;
}) => (
    <span
        className={classNames(
            "relative flex-none",
            member.you &&
                "after:absolute after:-inset-1 after:rounded-full after:border-2 after:border-[#f8765b] after:content-['']",
        )}
        style={{width: size, height: size}}
        title={member.name}
    >
        <span
            className="absolute inset-0 flex items-center justify-center rounded-full leading-none font-semibold text-white"
            style={{backgroundColor: member.color, fontSize: Math.round(size * 0.4)}}
        >
            {member.name[0]}
        </span>
        {member.imageUrl != null && (
            <img
                src={member.imageUrl}
                alt={member.name}
                className="relative block size-full rounded-full object-cover"
                onError={(event) => {
                    event.currentTarget.style.display = "none";
                }}
            />
        )}
        {rank != null && (
            // The avatar badge is the ONLY rank indicator — no number column.
            // z-10 lifts it above the wrapper's ::after "you" ring, so it sits
            // on top of the ring instead of the ring cutting through.
            <span
                className={classNames(
                    "absolute -right-1 -bottom-1 z-10 flex size-5 items-center justify-center rounded-full border-2 border-white text-[11px] font-bold dark:border-slate-900",
                    rank < 3 ? "text-white" : "text-stone-600 dark:text-slate-300",
                )}
                style={{
                    backgroundColor: rank < 3 ? MEDAL_COLORS[rank] : BADGE,
                }}
            >
                {rank + 1}
            </span>
        )}
        {showLive && member.live && <WalkBadge />}
    </span>
);

const Ring = ({
    pct,
    label,
    size = 42,
    empty = false,
}: {
    pct: number;
    label: React.ReactNode;
    size?: number;
    empty?: boolean;
}) => {
    if (empty) {
        return (
            <div
                className="flex-none rounded-full border-2 border-dashed"
                style={{width: size, height: size, borderColor: DASH}}
            />
        );
    }
    if (pct >= 1) {
        return (
            <div
                className="flex flex-none items-center justify-center rounded-full font-semibold tracking-tight text-white"
                style={{
                    width: size,
                    height: size,
                    backgroundColor: IND,
                    fontSize: Math.round(size * 0.3),
                }}
            >
                {label}
            </div>
        );
    }
    const strokeWidth = 4;
    const radius = (size - strokeWidth) / 2 - 1;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference * (1 - Math.max(pct, 0.02));
    return (
        <div
            className="relative flex flex-none items-center justify-center"
            style={{width: size, height: size}}
        >
            <svg width={size} height={size} className="absolute inset-0">
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={TRACK}
                    strokeWidth={strokeWidth}
                />
                {pct > 0 && (
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        fill="none"
                        stroke={IND}
                        strokeWidth={strokeWidth}
                        strokeLinecap="round"
                        strokeDasharray={circumference.toFixed(1)}
                        strokeDashoffset={offset.toFixed(1)}
                        transform={`rotate(-90 ${size / 2} ${size / 2})`}
                    />
                )}
            </svg>
            <span
                className="relative font-semibold tracking-tighter text-stone-700 dark:text-slate-200"
                style={{fontSize: Math.round(size * 0.27)}}
            >
                {label}
            </span>
        </div>
    );
};

const Pulse = ({
    member,
    max,
    markLive = false,
}: {
    member: BoardMember;
    max: number;
    markLive?: boolean;
}) => {
    const height = 24;
    const color = member.you ? CORAL : IND;
    const lastIndex = member.hourly.reduce(
        (last, value, index) => (value > 0 ? index : last),
        -1,
    );
    return (
        <div className="flex w-full items-end justify-between" style={{height}}>
            {member.hourly.map((value, index) => {
                const barHeight =
                    value > 0 ? Math.max((value / max) * (height - 3), 2) : 1.5;
                const live = markLive && member.live && index === lastIndex;
                return (
                    <span
                        key={index}
                        className={classNames(
                            "w-2.5 rounded-[3px]",
                            live && "animate-pulse motion-reduce:animate-none",
                        )}
                        style={{
                            height: barHeight,
                            backgroundColor: value > 0 ? color : TRACK,
                        }}
                    />
                );
            })}
        </div>
    );
};

const SkeletonBars = () => {
    const height = 24;
    return (
        <div className="flex w-full items-end justify-between" style={{height}}>
            {Array.from({length: 10}, (_, index) => (
                <span
                    key={index}
                    className="w-2.5 animate-pulse rounded-[3px] motion-reduce:animate-none"
                    style={{
                        height: 4 + ((index * 37) % 13),
                        backgroundColor: TRACK,
                        animationDelay: `${(index * 0.12).toFixed(2)}s`,
                    }}
                />
            ))}
        </div>
    );
};

// Grey alone reads as a rendering fault; the row says what it is. It sits
// inside the row's filtered content, so it desaturates and dims along with
// everything else rather than needing its own colour.
const RestingNote = ({label}: {label: string}) => (
    <span className="text-[12.5px] font-medium text-stone-500 dark:text-slate-400">
        {" "}
        · {label}
    </span>
);

const MemberName = ({member, solo = false}: {member: BoardMember; solo?: boolean}) => (
    <span
        className={classNames(
            "block text-[15px] font-medium",
            solo ? "mb-0" : "mb-[3px]",
        )}
    >
        {member.name}
        {member.you && (
            <span className="text-[12.5px] font-semibold text-[#5546e5]"> · you</span>
        )}
        {member.resting != null && <RestingNote label={member.resting} />}
    </span>
);

// Shared by TodayRow and WeekCard so the two card types cannot diverge.
const YOU_CARD =
    "bg-[#f8f7fe] ring-[1.5px] ring-[#c6c1f6] dark:bg-[#232136] dark:ring-[#5546e5]";
// Docked, the card floats over the list instead of hiding it.
const DOCKED_CARD = "bg-[#f8f7fe]/75! backdrop-blur-xl dark:bg-slate-800/75!";
// Out of the running. The filter lands on the card's CONTENTS, never the card
// itself: it is the walker who is benched, not the row. Draining the card's
// own surface punched a hole in the list on dark, and it also took the "you"
// treatment with it — the lilac fill and ring are how you find yourself, and
// they have to survive sitting a week out.
//
// Light needs nothing else: grey against white already recedes. Dark does not
// work that way — a mid-grey sitting on slate-900 is BRIGHTER than the indigo
// it replaced, so the benched row would shout. Dimming is what "recede" means
// on a dark scheme, which is why this is a per-scheme treatment rather than
// one filter for both.
const RESTING_CARD = "*:grayscale dark:*:brightness-75";

const TodayRow = ({
    member,
    rank,
    goal,
    max,
    liveBadges = false,
    noYou = false,
    docked = false,
}: {
    member: BoardMember;
    rank: number | null;
    goal: number;
    max: number;
    liveBadges?: boolean;
    noYou?: boolean;
    docked?: boolean;
}) => (
    <div
        className={classNames(
            "flex items-center gap-2.5 rounded-2xl bg-white px-3.5 py-[11px] dark:bg-slate-900",
            !noYou && member.you && YOU_CARD,
            docked && DOCKED_CARD,
            member.resting != null && RESTING_CARD,
        )}
    >
        <Avatar member={member} rank={rank} showLive={liveBadges} />
        <div className="flex-1">
            <MemberName member={member} />
            <Pulse member={member} max={max} markLive={liveBadges} />
        </div>
        <Ring
            pct={member.todayTotal / goal}
            label={<StepsK value={member.todayTotal} />}
        />
    </div>
);

type BoardTab = "today" | "week";

interface RankedRow {
    member: BoardMember;
    // null for members resting the week — they are on the board, not in
    // the running.
    rank: number | null;
}

// The one place row order and rank are decided, so the list and the docked
// copy of your own row cannot disagree about where you stand. Resting
// members sink below the field, still sorted among themselves by steps.
const rankedRows = (board: BoardData, tab: BoardTab): RankedRow[] => {
    const sorted = tab === "today" ? byToday(board) : byWeek(board);
    return [
        ...sorted
            .filter((member) => member.resting == null)
            .map((member, index) => ({member, rank: index})),
        ...sorted
            .filter((member) => member.resting != null)
            .map((member) => ({member, rank: null})),
    ];
};

const BottomToggle = ({
    selected,
    onSelect,
}: {
    selected: BoardTab;
    onSelect: (tab: BoardTab) => void;
}) => (
    // Margins and width live on BoardBottom's toggle row, which also seats
    // the week paging buttons beside this pill.
    <div className="flex flex-none rounded-full bg-white/65 p-1.5 shadow-[0_5px_20px_rgba(28,25,23,.10)] backdrop-blur-xl dark:bg-slate-800/65">
        {(["today", "week"] as const).map((tab) => (
            <button
                key={tab}
                type="button"
                onClick={() => {
                    void hardware.haptic();
                    void analytics.track("WALKING_CLUB_BOARD_TAB_SELECTED", {
                        walking_club_tab: tab,
                    });
                    onSelect(tab);
                }}
                className="relative flex-1 py-3 text-[16px]"
            >
                {selected === tab && (
                    // Fully-round keeps the selection concentric with the outer
                    // pill across the p-1.5 gap — and the radius must live in
                    // style, not className: layout animations distort
                    // class-specified radii.
                    <motion.span
                        layoutId="walking-club-toggle"
                        className="absolute inset-0 bg-stone-200/75 dark:bg-slate-600/75"
                        style={{borderRadius: 9999}}
                        transition={{type: "spring", stiffness: 500, damping: 35}}
                    />
                )}
                {/* A block flex, not inline-flex in a text-center line box:
                    inline-level content sits on the button's text baseline,
                    which is what kept pushing the label off-center. */}
                <span
                    className={classNames(
                        "relative z-10 flex items-center justify-center gap-1.5 leading-none",
                        selected === tab
                            ? "font-medium text-stone-900 dark:text-slate-100"
                            : "font-normal text-stone-600 dark:text-slate-400",
                    )}
                >
                    {tab === "today" ? (
                        <SunIcon className="size-5" />
                    ) : (
                        <CalendarDaysIcon className="size-5" />
                    )}
                    {tab === "today" ? "Today" : "Week"}
                </span>
            </button>
        ))}
    </div>
);

const DayDot = ({
    value,
    goal,
    size = 18,
}: {
    value: number | null;
    goal: number;
    size?: number;
}) => {
    if (value == null) {
        return (
            <span
                className="flex-none rounded-full border-[1.5px] border-dashed"
                style={{width: size, height: size, borderColor: DASH}}
            />
        );
    }
    if (value >= goal) {
        return (
            <span
                className="inline-flex flex-none items-center justify-center rounded-full"
                style={{width: size, height: size, backgroundColor: IND}}
            >
                <svg
                    viewBox="0 0 12 12"
                    style={{width: size * 0.58, height: size * 0.58}}
                >
                    <path
                        d="M2.8 6.4l2.2 2.2L9.4 3.8"
                        fill="none"
                        stroke="#fff"
                        strokeWidth="2.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </span>
        );
    }
    const strokeWidth = 2.5;
    const radius = (size - strokeWidth) / 2 - 0.5;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference * (1 - Math.max(value / goal, 0.05));
    return (
        <span className="flex-none" style={{width: size, height: size}}>
            <svg width={size} height={size} className="block">
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={TRACK}
                    strokeWidth={strokeWidth}
                />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={IND}
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    strokeDasharray={circumference.toFixed(1)}
                    strokeDashoffset={offset.toFixed(1)}
                    transform={`rotate(-90 ${size / 2} ${size / 2})`}
                />
            </svg>
        </span>
    );
};

const WEEK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const dayWinner = (board: BoardData, dayIndex: number): BoardMember | null => {
    if (dayIndex > board.lastCompleteWeekday) {
        return null;
    }
    const best = board.members.reduce<BoardMember | null>((winner, member) => {
        const value = member.daily[dayIndex];
        if (value == null || value <= 0 || member.resting != null) {
            return winner;
        }
        if (winner == null || value > (winner.daily[dayIndex] ?? 0)) {
            return member;
        }
        return winner;
    }, null);
    return best;
};

// The dashed empty-day circle, drawn exactly like LearningWeek's DashedCircle
// (stroke 5 in a 100 viewBox, 12/8 dash) so both week headers read the same.
const WinnerDash = () => (
    <span className="relative block size-10">
        <svg viewBox="0 0 100 100" className="absolute inset-0 size-full">
            <circle
                cx={50}
                cy={50}
                r={47.5}
                fill="none"
                stroke={DASH}
                strokeWidth={5}
                strokeDasharray="12 8"
            />
        </svg>
    </span>
);

const WinnerHead = ({board}: {board: BoardData}) => (
    <div className="grid flex-none grid-cols-7">
        {WEEK_DAYS.map((day, index) => {
            const winner = dayWinner(board, index);
            return (
                <div key={day} className="flex flex-col items-center gap-1">
                    <span className="text-sm font-medium text-gray-500 dark:text-slate-400">
                        {day}
                    </span>
                    {winner != null ? (
                        <span className="relative size-10">
                            <span
                                className="absolute inset-0 flex items-center justify-center rounded-full text-[15px] leading-none font-bold text-white"
                                style={{backgroundColor: winner.color}}
                            >
                                {winner.name[0]}
                            </span>
                            {winner.imageUrl != null && (
                                <img
                                    src={winner.imageUrl}
                                    alt=""
                                    className="relative block size-10 rounded-full object-cover shadow-[0_0_0_2px_#e0a63b]"
                                    onError={(event) => {
                                        event.currentTarget.style.display = "none";
                                    }}
                                />
                            )}
                        </span>
                    ) : (
                        <WinnerDash />
                    )}
                </div>
            );
        })}
    </div>
);

const WeekPulse = ({member, max}: {member: BoardMember; max: number}) => {
    const height = 26;
    const color = member.you ? CORAL : IND;
    return (
        <div className="flex w-full justify-between" style={{height}}>
            {Array.from({length: 7}, (_, index) => {
                const value = member.daily[index];
                return (
                    <span
                        key={index}
                        className="flex w-[18px] flex-none items-end justify-center"
                    >
                        <span
                            className="w-3 rounded-[3px]"
                            style={
                                value != null
                                    ? {
                                          height: Math.max(
                                              (value / max) * (height - 3),
                                              2,
                                          ),
                                          backgroundColor: color,
                                      }
                                    : {height: 1.5, backgroundColor: TRACK}
                            }
                        />
                    </span>
                );
            })}
        </div>
    );
};

const WeekCard = ({
    member,
    rank,
    goal,
    max,
    docked = false,
}: {
    member: BoardMember;
    rank: number | null;
    goal: number;
    max: number;
    docked?: boolean;
}) => (
    <div
        className={classNames(
            "flex items-center gap-3 rounded-[18px] bg-white px-3.5 py-[9px] dark:bg-slate-900",
            member.you && YOU_CARD,
            docked && DOCKED_CARD,
            member.resting != null && RESTING_CARD,
        )}
    >
        <Avatar member={member} rank={rank} size={54} />
        <div className="min-w-0 flex-1">
            <div className="mb-[2px] text-left text-[13px] font-normal text-stone-700 dark:text-slate-300">
                {member.name}
                {member.you && " · you"}
                {member.resting != null && ` · ${member.resting}`}
            </div>
            <WeekPulse member={member} max={max} />
            <div className="mt-[5px] flex justify-between">
                {Array.from({length: 7}, (_, dayIndex) => (
                    <DayDot key={dayIndex} value={member.daily[dayIndex]} goal={goal} />
                ))}
            </div>
        </div>
        <span className="w-[66px] flex-none text-right text-xl font-bold tracking-tight tabular-nums">
            <StepsK value={member.weekTotal} />
        </span>
    </div>
);

// Which row docks on this board, or null when the board cannot dock at all.
// The dock itself and the scroll space reserved for it MUST decide from this
// one function: the rule was hand-copied into both once, they drifted, and a
// board that lost members below the threshold kept a card of blank scroll
// under its last row with nothing to fill it.
const dockRow = (board: BoardData, tab: BoardTab): RankedRow | null => {
    // With one or two rows the docked copy just duplicates a row already on
    // screen — only pin it once the list is long enough to lose your place.
    if (board.members.length < 3) {
        return null;
    }
    return rankedRows(board, tab).find((row) => row.member.you) ?? null;
};

const Dock = ({board, tab}: {board: BoardData; tab: BoardTab}) => {
    const me = dockRow(board, tab);
    if (me == null) {
        return null;
    }
    // The identical ranked card, pinned — only the shadow marks it as docked.
    return (
        <div className="mb-joy-step flex-none rounded-[18px] shadow-[0_-2px_14px_rgba(28,25,23,.10)]">
            {tab === "today" ? (
                <TodayRow
                    member={me.member}
                    rank={me.rank}
                    goal={board.goal}
                    max={maxHourly(board)}
                    liveBadges
                    docked
                />
            ) : (
                <WeekCard
                    member={me.member}
                    rank={me.rank}
                    goal={board.goal}
                    max={maxDaily(board)}
                    docked
                />
            )}
        </div>
    );
};

const Logo = ({menu}: {menu?: React.ReactNode}) => (
    <div className="relative z-10 mt-0.5 mb-3.5 flex flex-none items-center justify-center">
        {menu != null && (
            <div className="absolute top-1/2 left-0 -translate-y-1/2">{menu}</div>
        )}
        <graphics.Logo className="h-7 w-auto" />
    </div>
);

// The board's header content. It stays full size on scroll, like every header.
const ClubTitle = () => (
    <div className="flex items-center justify-center py-0.5">
        <graphics.Logo className="club-wordmark h-7 w-auto" />
    </div>
);

const MenuButton = () => {
    const isMenuOpen = useAppSelector((state) => state.scenes.isMenuOpen);
    const dispatch = useAppDispatch();
    return (
        <MenuIcon
            isMenuOpen={isMenuOpen}
            toggleMenu={() => dispatch({type: "TOGGLE_MENU"})}
        />
    );
};

const StepDots = ({active}: {active: number}) => (
    <div className="flex justify-center gap-1.5">
        {[0, 1, 2].map((index) => (
            <motion.i
                key={index}
                layout
                className={classNames(
                    "h-1.5 rounded-full",
                    index === active ? "w-4 bg-[#5546e5]" : "w-1.5 bg-[#d6d3d1]",
                )}
                transition={{type: "spring", stiffness: 500, damping: 35}}
            />
        ))}
    </div>
);

const GateButton = ({
    label,
    onClick,
    disabled = false,
    isSubmitting = false,
    animateIcon = false,
}: {
    label: string;
    onClick: () => void | Promise<void>;
    disabled?: boolean;
    isSubmitting?: boolean;
    animateIcon?: boolean;
}) => (
    <div className="mt-2 flex justify-center">
        <AnimatedButton
            label={label}
            icon={ArrowRightIcon}
            iconPosition="right"
            animateIcon={animateIcon}
            onClick={onClick}
            isSubmitting={isSubmitting}
            disabled={disabled}
        />
    </div>
);

const GhostButton = ({label, onClick}: {label: string; onClick: () => void}) => (
    <button
        type="button"
        onClick={() => {
            void hardware.haptic();
            onClick();
        }}
        className="mt-3.5 block w-full text-[13.5px] font-semibold text-stone-600"
    >
        {label}
    </button>
);

const GateInput = (props: React.InputHTMLAttributes<HTMLInputElement>) => (
    <input
        {...props}
        className="mb-joy-step block h-14 w-full rounded-[14px] border border-stone-200 bg-white px-4 text-[15px] text-stone-900 placeholder:text-stone-400 focus:border-[#c6c1f6] focus:ring-2 focus:ring-[#c6c1f6]/60 focus:outline-none disabled:bg-stone-50 disabled:text-stone-500"
    />
);

const MyRowPreview = ({
    name,
    photo,
    error = false,
}: {
    name: string;
    photo: string | null;
    error?: boolean;
}) => (
    <div className="mb-4 flex items-center gap-2.5 rounded-2xl bg-white py-[11px] pr-3.5 pl-3 text-left">
        <span className="relative size-[38px] flex-none">
            <span className="absolute inset-0 flex items-center justify-center rounded-full bg-[#f8765b] text-[15px] font-semibold text-white">
                {name === "" ? "?" : name[0]}
            </span>
            {photo != null && (
                <img
                    src={photo}
                    alt={name}
                    className="relative block size-full rounded-full object-cover"
                />
            )}
        </span>
        <div className="flex-1">
            <span className="mb-[3px] block text-[15px] font-medium">
                {name === "" ? "You" : name}
                <span className="text-[12.5px] font-semibold text-[#5546e5]">
                    {" "}
                    · you
                </span>
            </span>
            <SkeletonBars />
        </div>
        {error ? (
            <span className="flex size-[42px] flex-none items-center justify-center rounded-full border-2 border-dashed border-[#f3b4ae]">
                <span className="text-[16px] font-bold text-[#d92d20]">!</span>
            </span>
        ) : (
            <Ring pct={0} label="" empty />
        )}
    </div>
);

const HEALTH_PATH = ["Health", "Sharing", "Apps", "JOY", "Steps"];

const HealthPathChips = () => (
    <div className="mb-5 flex w-full items-center justify-between whitespace-nowrap">
        {HEALTH_PATH.map((step, index) => (
            <React.Fragment key={step}>
                {index > 0 && (
                    <span className="text-[12px] font-light text-stone-400">›</span>
                )}
                <span
                    className={classNames(
                        "inline-flex h-[30px] items-center rounded-[10px] px-2 text-[12px] font-medium",
                        index === HEALTH_PATH.length - 1
                            ? "bg-[#5546e5] text-white"
                            : "bg-white text-stone-700",
                    )}
                >
                    {step === "JOY" ? (
                        <graphics.Logo className="h-[0.75em] w-auto overflow-visible" />
                    ) : (
                        step
                    )}
                </span>
            </React.Fragment>
        ))}
    </div>
);

type GateRoute =
    | "email"
    | "email_exists"
    | "link_sent"
    | "profile"
    | "connect"
    | "denied";
// The design-review rig (?step=) covers the gate wizard only — its steps sit
// behind a real emailed magic link, so canned previews earn their keep. The
// board needs no rig: a seeded preview renders the real thing.
const DEMO_ROUTES: GateRoute[] = [
    "email",
    "email_exists",
    "profile",
    "connect",
    "denied",
];

const FrozenBoard = () => (
    <div className="flex flex-1 flex-col gap-joy-step overflow-hidden">
        {byToday(DEMO_BOARD)
            .filter((member) => !member.you)
            .map((member, index) => (
                <TodayRow
                    key={member.uuid}
                    member={member}
                    rank={index}
                    goal={DEMO_BOARD.goal}
                    max={maxHourly(DEMO_BOARD)}
                    noYou
                />
            ))}
    </div>
);

const Veil = () => (
    <div
        className="absolute inset-0 backdrop-blur-[7px] backdrop-saturate-[.7]"
        style={{
            background:
                "linear-gradient(180deg,rgba(245,245,244,.62) 0%,rgba(245,245,244,.9) 40%,rgba(245,245,244,.96) 100%)",
        }}
    />
);

const PHOTO_INPUT_ID = "walking-club-photo-input";

const dataUrlToFile = async (dataUrl: string): Promise<File> => {
    const blob = await (await fetch(dataUrl)).blob();
    return new File([blob], "walking-club-photo.jpg", {type: blob.type});
};

const uploadPhoto = async (dataUrl: string): Promise<string | null> => {
    const uploadUrlResult = await server.core.rpc.create_user_media_image({
        ignored: "",
    });
    if (uploadUrlResult.type !== "success") {
        return null;
    }
    const {image_uid, upload_url} = uploadUrlResult.data;
    const file = await dataUrlToFile(dataUrl);
    const resized = await resizeImage(file, 512);
    const uploadForm = new FormData();
    uploadForm.append("file", resized ?? file, file.name);
    const response = await fetch(upload_url, {body: uploadForm, method: "POST"});
    return response.ok ? image_uid : null;
};

const GateScreen = ({
    code,
    clubName,
    todayTotal,
    loggedInName,
    demo,
    prefillsAllowed = false,
    initialRoute,
    onDone,
}: {
    code: string;
    clubName: string;
    todayTotal: number;
    loggedInName: string | null;
    demo: boolean;
    // Prefills allowed without demo short-circuits: the real flow on
    // non-production environments can skip the photo picker (the native
    // shell's file input needs an NSPhotoLibraryUsageDescription it does not
    // ship yet), while every handler still runs for real.
    prefillsAllowed?: boolean;
    initialRoute: GateRoute;
    onDone: () => void;
}) => {
    const dispatch = useAppDispatch();
    const profile = useAppSelector(orm.getProfile);
    const [route, setRoute] = React.useState<GateRoute>(initialRoute);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [accountState, setAccountState] = React.useState<
        "unknown" | "new" | "existing" | "auto_logged_in"
    >(loggedInName != null ? "existing" : "unknown");

    // The wizard steps are component state, not routes — SCENE_VIEW cannot
    // see where someone abandons, so each step reports itself.
    React.useEffect(() => {
        if (demo) {
            return;
        }
        void analytics.track("WALKING_CLUB_ONBOARDING_STEP_VIEWED", {
            walking_club_step: match(route)
                .with("email", () => "email" as const)
                .with("email_exists", () => "existing_account" as const)
                .with("link_sent", () => "login_link_sent" as const)
                .with("profile", () => "profile" as const)
                .with("connect", () => "apple_health" as const)
                .with("denied", () => "apple_health_help" as const)
                .exhaustive(),
            walking_club_account_state: accountState,
            walking_club_member_type:
                loggedInName != null ? "existing_full_user" : "new_boxed_user",
        });
    }, [route]);

    // Screenshot prefills: ?email=…&name=…&photo=demo
    const [params] = React.useState(() =>
        typeof window === "undefined"
            ? null
            : new URLSearchParams(window.location.search),
    );
    const [email, setEmail] = React.useState(params?.get("email") ?? "");
    const [name, setName] = React.useState(params?.get("name") ?? loggedInName ?? "");
    const [photo, setPhoto] = React.useState<string | null>(
        (demo || prefillsAllowed) && params?.get("photo") === "demo"
            ? "https://i.pravatar.cc/96?img=68"
            : null,
    );

    const emailValid = /.+@.+\..+/.test(email.trim());
    const profileComplete = name.trim() !== "" && photo != null;
    const isLoggedIn = loggedInName != null;

    const pickPhoto = () => {
        const input = document.getElementById(PHOTO_INPUT_ID);
        if (input instanceof HTMLInputElement) {
            input.value = "";
            input.click();
        }
    };

    const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file == null) {
            return;
        }
        void analytics.track("WALKING_CLUB_PHOTO_SELECTED");
        const reader = new FileReader();
        reader.onload = () => {
            setPhoto(typeof reader.result === "string" ? reader.result : null);
        };
        reader.readAsDataURL(file);
    };

    const handleEmail = async () => {
        if (demo) {
            setRoute(
                email.trim().toLowerCase().startsWith("silvio")
                    ? "email_exists"
                    : "profile",
            );
            return;
        }
        setIsSubmitting(true);
        setError(null);
        const result = await server.core.rpc.check_email(email.trim());
        setIsSubmitting(false);
        if (result.type !== "success") {
            setError("Something went wrong. Try again.");
            return;
        }
        if (result.data === false) {
            setAccountState("new");
            setRoute("profile");
        } else if (result.data === true) {
            setAccountState("existing");
            setRoute("email_exists");
        } else {
            // A fresh passwordless account: check_email logged us in.
            setAccountState("auto_logged_in");
            await dispatch(auth.postLogin(result.data, false));
            setName(result.data.name ?? "");
            setRoute("profile");
        }
    };

    const handleSignIn = async () => {
        if (demo) {
            setRoute("link_sent");
            return;
        }
        setIsSubmitting(true);
        const result = await server.journal.clubs.walking_club_login_link({
            code,
            email: email.trim(),
        });
        setIsSubmitting(false);
        if (result.type !== "success") {
            setError("Something went wrong. Try again.");
        } else if (typeof result.data === "boolean") {
            setRoute("link_sent");
        } else {
            // A fresh passwordless account signs in without the email round
            // trip.
            setAccountState("auto_logged_in");
            await dispatch(auth.postLogin(result.data, false));
            setName(result.data.name ?? "");
            setRoute("profile");
        }
    };

    const handleProfile = async () => {
        if (demo) {
            setRoute("connect");
            return;
        }
        setIsSubmitting(true);
        setError(null);
        try {
            if (!isLoggedIn) {
                const {platform} = await hardware.getAppInfo();
                void analytics.track("REGISTERING", {
                    registration_method: "walking_club",
                });
                const registration =
                    await server.journal.clubs.walking_club_registration({
                        registration: {
                            email: email.trim(),
                            with_notification: false,
                            invite_code: "walking_club",
                            recipe_uuid: null,
                            source: null,
                            platform,
                        },
                        code,
                        display_name: name.trim(),
                        image_uid: null,
                    });
                if (registration.type !== "success") {
                    void analytics.track("WALKING_CLUB_JOIN_FAILED", {
                        walking_club_failure_stage: "registration",
                    });
                    setError("Could not sign you up. Try again.");
                    return;
                }
                void analytics.registered(
                    registration.data.user.tracking_key,
                    "walking_club",
                    "trial",
                );
                await dispatch(auth.postLogin(registration.data, true));
            }
            // A failed upload must never block joining — the board falls back
            // to the initial avatar and the photo can be re-attached later.
            const imageUid =
                photo != null ? await uploadPhoto(photo).catch(() => null) : null;
            if (photo != null && imageUid == null) {
                void analytics.track("WALKING_CLUB_PHOTO_UPLOAD_FAILED");
            }
            const join = await server.journal.clubs.walking_club_join({
                code,
                display_name: name.trim(),
                image_uid: imageUid,
            });
            if (join.type !== "success") {
                void analytics.track("WALKING_CLUB_JOIN_FAILED", {
                    walking_club_failure_stage: "membership",
                });
                setError("Could not join the club. Try again.");
                return;
            }
            // Joining can retire a solo learning week server-side, and the
            // journal picks its mode from the profile in the store. Without
            // this the session keeps the stale flag and keeps rendering the
            // restricted journal until the app is next booted.
            await dispatch(orm.fetchProfile());
            setRoute("connect");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleConnect = async () => {
        if (demo) {
            onDone();
            return;
        }
        const {platform} = await hardware.getAppInfo();
        if (platform !== "ios") {
            // iOS only for now: web and Android skip the HealthKit gate and see
            // the board without their own pulse.
            onDone();
            return;
        }
        setIsSubmitting(true);
        void analytics.track("REQUESTED_APPLE_HEALTH_AUTHORIZATION");
        try {
            await hardware.HealthPlugin.requestAuthorization({
                read: ["stepCount"],
                write: [],
                all: [],
            });
            // Authorization alone arms nothing: every sync path
            // (checkAndSyncHealthData on boot, observer, resume) gates on this
            // device setting, so without it a club-connected user never syncs
            // a single step.
            if (profile != null) {
                const {deviceUUID} = await hardware.getAppInfo();
                const updatedProfile = produce(profile, (draft) => {
                    draft.settings.by_device = draft.settings.by_device ?? {};
                    const device = (draft.settings.by_device[deviceUUID] ??= {});
                    device.apple_health = {status: "ENABLED"};
                });
                dispatch({type: "SET_PROFILE", profile: updatedProfile});
                void server.core.rpc.update_settings(updatedProfile.settings);
                void healthkit.syncHealthData(
                    updatedProfile,
                    7,
                    "walking-club-connect",
                );
            }
            onDone();
        } catch {
            void analytics.track("APPLE_HEALTH_AUTHORIZATION_REQUEST_ERROR");
            setRoute("denied");
        } finally {
            setIsSubmitting(false);
        }
    };

    const content = match(route)
        .with("email", () => (
            <>
                <h3 className="mb-1.5 text-[27px] leading-[1.3] font-light">
                    Your club walked{" "}
                    <b className="font-normal text-[#5546e5]">{formatK(todayTotal)}</b>{" "}
                    today
                </h3>
                <p className="mb-5 text-[14px] leading-relaxed font-light text-stone-600">
                    Enter your email to get on the board.
                </p>
                <GateInput
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                />
                <GateButton
                    label="Continue"
                    disabled={!emailValid || isSubmitting}
                    isSubmitting={isSubmitting}
                    animateIcon
                    onClick={handleEmail}
                />
            </>
        ))
        .with("email_exists", () => (
            <>
                <h3 className="mb-1.5 text-[27px] leading-[1.3] font-light">
                    Welcome back
                </h3>
                <p className="mb-5 text-[14px] leading-relaxed font-light text-stone-600">
                    This email already has a{" "}
                    <graphics.Logo className="inline h-[0.75em] w-auto overflow-visible align-baseline" />{" "}
                    account.
                </p>
                <GateInput type="email" value={email} disabled />
                <GateButton
                    label="Sign in"
                    disabled={isSubmitting}
                    isSubmitting={isSubmitting}
                    onClick={handleSignIn}
                />
                <GhostButton
                    label="Use a different email"
                    onClick={() => setRoute("email")}
                />
            </>
        ))
        .with("link_sent", () => (
            <>
                <h3 className="mb-1.5 text-[27px] leading-[1.3] font-light">
                    Check your email
                </h3>
                <p className="mb-5 text-[14px] leading-relaxed font-light text-stone-600">
                    We sent a sign-in link to {email} — it signs you in and brings you
                    right back here.
                </p>
                <GhostButton
                    label="Use a different email"
                    onClick={() => setRoute("email")}
                />
            </>
        ))
        .with("profile", () => (
            <>
                <h3 className="mb-1.5 text-[27px] leading-[1.3] font-light">
                    Who’s walking?
                </h3>
                <p className="mb-5 text-[14px] leading-relaxed font-light text-stone-600">
                    The board runs on faces — add yours.
                </p>
                <button
                    type="button"
                    onClick={pickPhoto}
                    className="mb-joy-step flex h-14 w-full items-center gap-3 rounded-[14px] border border-stone-200 bg-white pr-3.5 pl-2.5"
                >
                    <span className="flex size-9 flex-none items-center justify-center overflow-hidden rounded-full bg-[#f1efee]">
                        {photo != null ? (
                            <img
                                src={photo}
                                alt="Your photo"
                                className="size-full object-cover"
                            />
                        ) : (
                            <CameraIcon className="size-5 text-stone-400" />
                        )}
                    </span>
                    <span
                        className={classNames(
                            "flex-1 text-left text-[15px]",
                            photo != null ? "text-stone-900" : "text-stone-400",
                        )}
                    >
                        {photo != null ? "Change your photo" : "Add your photo"}
                    </span>
                    {photo != null ? (
                        <span className="flex size-5 flex-none items-center justify-center rounded-full bg-[#5546e5]">
                            <svg viewBox="0 0 12 12" className="size-3">
                                <path
                                    d="M2.8 6.4l2.2 2.2L9.4 3.8"
                                    fill="none"
                                    stroke="#fff"
                                    strokeWidth="2.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                    ) : (
                        <span className="flex-none text-lg text-[#d6d3d1]">›</span>
                    )}
                </button>
                <input
                    type="file"
                    id={PHOTO_INPUT_ID}
                    accept="image/*"
                    className="hidden"
                    onChange={handlePhotoChange}
                />
                <GateInput
                    type="text"
                    placeholder="What should we call you?"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                />
                <GateButton
                    label="Continue"
                    disabled={!profileComplete || isSubmitting}
                    isSubmitting={isSubmitting}
                    animateIcon
                    onClick={handleProfile}
                />
            </>
        ))
        .with("connect", () => (
            <>
                <h3 className="mb-1.5 text-[27px] leading-[1.3] font-light">
                    Start your pulse
                </h3>
                <p className="mb-5 text-[14px] leading-relaxed font-light text-stone-600">
                    This is your spot. Connecting steps fills it in —{" "}
                    <graphics.Logo className="inline h-[0.75em] w-auto overflow-visible align-baseline" />{" "}
                    reads your daily step count from Apple Health, nothing else.
                </p>
                <MyRowPreview name={name === "" ? "You" : name} photo={photo} />
                <GateButton
                    label="Connect Apple Health"
                    disabled={isSubmitting}
                    isSubmitting={isSubmitting}
                    animateIcon
                    onClick={handleConnect}
                />
            </>
        ))
        .with("denied", () => (
            <>
                <MyRowPreview name={name === "" ? "You" : name} photo={photo} error />
                <h3 className="mb-1.5 text-[27px] leading-[1.3] font-light">
                    Steps access is off
                </h3>
                <HealthPathChips />
                <GateButton
                    label="Open Health settings"
                    onClick={() => {
                        window.location.href = "x-apple-health://";
                    }}
                />
                <GhostButton label="Try again" onClick={() => void handleConnect()} />
            </>
        ))
        .exhaustive();

    return (
        <>
            <FrozenBoard />
            <Veil />
            <motion.div
                key={route}
                initial={{opacity: 0, y: 12}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.25}}
                className="absolute inset-0 flex flex-col justify-center px-4 pb-8 text-center"
            >
                {clubName !== "" && route === "email" && (
                    <span className="mb-2 text-[12.5px] font-semibold tracking-wide text-stone-500 uppercase">
                        {clubName}
                    </span>
                )}
                {error != null && (
                    <span className="mb-2 text-[12.5px] font-semibold text-[#d92d20]">
                        {error}
                    </span>
                )}
                {content}
            </motion.div>
            <div className="relative z-10 flex flex-none justify-center pt-2">
                <StepDots
                    active={match(route)
                        .with("email", "email_exists", "link_sent", () => 0)
                        .with("profile", () => 1)
                        .with("connect", "denied", () => 2)
                        .exhaustive()}
                />
            </div>
        </>
    );
};

// The nav bar and your docked card float over the board rather than sitting on
// an opaque strip: both are translucent and blurred, so rows read underneath.
// pb-safe rather than pb-safe-or-padding: the bar clears the home indicator on
// device without leaving 2rem of gap for rows to show through beneath it on a
// screen tall enough to notice.

// The week picker: a glass card above the calendar button listing every week
// the club has lived, newest first, by date range. The week being viewed is
// flagged with the toggle's own selected treatment.
const WeekMenu = ({
    currentWeekStart,
    weeksAvailable,
    weeksAgo,
    pagePending,
    onSelect,
    onClose,
}: {
    currentWeekStart: DateValue;
    weeksAvailable: number;
    weeksAgo: number;
    pagePending: boolean;
    onSelect: (next: number) => void;
    onClose: () => void;
}) => (
    <>
        {/* Invisible backdrop: a tap anywhere else dismisses the menu. */}
        <div className="fixed inset-0" onClick={onClose} />
        <motion.div
            initial={{opacity: 0, y: 10, scale: 0.96}}
            animate={{opacity: 1, y: 0, scale: 1}}
            exit={{opacity: 0, y: 10, scale: 0.96}}
            transition={{type: "spring", stiffness: 500, damping: 38}}
            className="absolute bottom-full left-[6%] mb-joy-step flex max-h-[45vh] w-60 flex-col gap-0.5 overflow-y-auto rounded-3xl bg-white/65 p-1.5 shadow-[0_5px_20px_rgba(28,25,23,.10)] backdrop-blur-xl dark:bg-slate-800/65"
        >
            {Array.from({length: weeksAvailable + 1}, (_, index) => (
                <button
                    key={index}
                    type="button"
                    disabled={pagePending}
                    onClick={() => {
                        void hardware.haptic();
                        onSelect(index);
                    }}
                    className={classNames(
                        "flex items-center gap-1.5 rounded-full px-4 py-2.5 text-left text-[15px] leading-none",
                        index === weeksAgo
                            ? "bg-stone-200/75 font-medium text-stone-900 dark:bg-slate-600/75 dark:text-slate-100"
                            : "font-normal text-stone-600 dark:text-slate-400",
                    )}
                >
                    {dates.weekLabel(dates.weekStartFor(currentWeekStart, index))}
                    {/* The one week still being walked, marked ever so
                        slightly. */}
                    {index === 0 && (
                        <span className="size-1.5 flex-none rounded-full bg-[#F57E5F]" />
                    )}
                </button>
            ))}
        </motion.div>
    </>
);

const BoardBottom = ({
    board,
    tab,
    setTab,
    weeksAgo,
    weeksAvailable,
    currentWeekStart,
    requestWeek,
    pagePending,
    showDock,
    toggleRef,
    dockRef,
}: {
    board: BoardData;
    tab: BoardTab;
    setTab: (tab: BoardTab) => void;
    weeksAgo: number;
    weeksAvailable: number;
    currentWeekStart: DateValue | null;
    requestWeek: (next: number) => void;
    pagePending: boolean;
    showDock: boolean;
    toggleRef: (element: HTMLElement | null) => void;
    dockRef: (element: HTMLElement | null) => void;
}) => {
    const [menuOpen, setMenuOpen] = React.useState(false);
    return (
        <div className={classNames("relative", CLUB_THEME)}>
            {/* The dock overlays the group (bottom-full) instead of sitting in
                its flow: its exit keeps the element mounted at full height
                until the animation completes, and an exit interrupted by the
                webview suspending never completes — which left an invisible
                dock-sized spacer above the toggle on device. Out of flow, a
                stuck exit can't move anything, and the toggle no longer jumps
                when the dock appears. Width matches the toggle row so the
                floating group reads as one instrument. */}
            <AnimatePresence>
                {showDock && (
                    <motion.div
                        ref={dockRef}
                        className="absolute inset-x-0 bottom-full mx-auto w-[88%]"
                        initial={{opacity: 0, y: 14}}
                        animate={{opacity: 1, y: 0}}
                        exit={{opacity: 0, y: 14}}
                        transition={{type: "spring", stiffness: 420, damping: 34}}
                    >
                        <Dock board={board} tab={tab} />
                    </motion.div>
                )}
            </AnimatePresence>
            <div ref={toggleRef} className="relative">
                <AnimatePresence>
                    {menuOpen && currentWeekStart != null && (
                        <WeekMenu
                            currentWeekStart={currentWeekStart}
                            weeksAvailable={weeksAvailable}
                            weeksAgo={weeksAgo}
                            pagePending={pagePending}
                            onSelect={(next) => {
                                setMenuOpen(false);
                                requestWeek(next);
                            }}
                            onClose={() => setMenuOpen(false)}
                        />
                    )}
                </AnimatePresence>
                {weeksAgo > 0 && (
                    <div className="mx-auto mb-joy-step w-fit rounded-full bg-white/65 px-3 py-1 text-[12.5px] font-medium text-stone-700 backdrop-blur-xl dark:bg-slate-800/65 dark:text-slate-300">
                        {dates.weekLabel(board.weekStart)}
                    </div>
                )}
                <div className="mx-auto mb-1 flex w-[88%] items-center gap-2">
                    {weeksAvailable > 0 && currentWeekStart != null && (
                        <button
                            type="button"
                            aria-label="Pick week"
                            onClick={() => {
                                void hardware.haptic();
                                setMenuOpen((open) => !open);
                            }}
                            className="flex size-[46px] flex-none items-center justify-center rounded-full bg-white/65 shadow-[0_5px_20px_rgba(28,25,23,.10)] backdrop-blur-xl dark:bg-slate-800/65"
                        >
                            <CalendarDaysIcon className="size-5 text-stone-700 dark:text-slate-300" />
                        </button>
                    )}
                    <div className="min-w-0 flex-1">
                        <BottomToggle selected={tab} onSelect={setTab} />
                    </div>
                </div>
            </div>
        </div>
    );
};

// How much of the viewport the floating nav bar covers, measured from the
// toggle's top edge to the bottom of the screen so margins and the home
// indicator's safe area are included. Measured off the toggle alone: the docked
// card grows the sticky group upwards without moving the toggle, so the band
// that decides whether to dock cannot be changed by docking.
const useBottomBarInset = () => {
    const [element, setElement] = React.useState<HTMLElement | null>(null);
    const [inset, setInset] = React.useState(0);

    React.useEffect(() => {
        if (element == null) return;

        const measure = () => {
            setInset(
                Math.max(0, window.innerHeight - element.getBoundingClientRect().top),
            );
        };
        measure();

        const observer = new ResizeObserver(measure);
        observer.observe(element);
        window.addEventListener("resize", measure);

        return () => {
            observer.disconnect();
            window.removeEventListener("resize", measure);
        };
    }, [element]);

    return {setElement, inset};
};

// The dock floats over the scroller (absolute, bottom-full), so what it
// covers is the END of the list — and from every other member's view the last
// row was unreachable at any scroll position (#6). Its height becomes the
// list's extra bottom padding while it is up, which is exactly the space it
// occupies: the gap only exists while something fills it, so the scroll-end
// spacing token (#1828) is untouched whenever the dock is away.
//
// Padding, not a layout change: the dock stays out of flow for the reason
// BoardBottom documents — an exit interrupted by the webview suspending would
// otherwise strand a spacer above the toggle. A stranded exit here leaves
// nothing worse than scrollable space below the last row.
const useDockInset = () => {
    const [element, setElement] = React.useState<HTMLElement | null>(null);
    const [inset, setInset] = React.useState(0);

    React.useEffect(() => {
        // NOT reset when the dock unmounts. The reservation has to outlive the
        // thing it reserves for: releasing it is a scrollHeight change, the
        // browser clamps scrollTop, and the list jumps under the finger
        // (#1818's lesson, relearned). Once measured, the clearance is a
        // property of the board, not of what is on screen right now.
        if (element == null) {
            return;
        }

        const measure = () => {
            const height = element.getBoundingClientRect().height;
            if (height > 0) {
                setInset(height);
            }
        };
        measure();

        const observer = new ResizeObserver(measure);
        observer.observe(element);

        return () => observer.disconnect();
    }, [element]);

    return {setElement, inset};
};

// Your own card is pinned at the bottom only while your row is off-screen —
// with it in view the docked copy is pure duplication. A row behind the
// translucent bar counts as off-screen: the point of the docked copy is that
// your number stays readable, and behind frosted glass it is not.
const useYourRowVisible = (bottomInset: number) => {
    const [element, setElement] = React.useState<HTMLElement | null>(null);
    const [isVisible, setIsVisible] = React.useState(true);

    React.useEffect(() => {
        if (element == null) {
            // Between tab switches there is no row registered; a stale
            // "hidden" here would flash the other tab's docked card over a
            // plainly visible row.
            setIsVisible(true);
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                // The newest entry of a coalesced batch; isIntersecting is
                // any-overlap, so the ratio is what the 60% legibility rule
                // actually means.
                const entry = entries.at(-1);
                if (entry != null) {
                    setIsVisible(entry.intersectionRatio >= 0.6);
                }
            },
            {
                threshold: [0.6],
                rootMargin: `0px 0px -${Math.round(bottomInset)}px 0px`,
            },
        );
        observer.observe(element);

        return () => observer.disconnect();
    }, [element, bottomInset]);

    return {setElement, isVisible};
};

const Board = ({
    board,
    tab,
    rowRef,
    dockInset = 0,
}: {
    board: BoardData;
    tab: BoardTab;
    rowRef?: (element: HTMLElement | null) => void;
    dockInset?: number;
}) => {
    const rows = React.useMemo(() => rankedRows(board, tab), [tab, board]);
    const max = React.useMemo(
        () => (tab === "today" ? maxHourly(board) : maxDaily(board)),
        [tab, board],
    );

    return (
        <>
            {tab === "week" && <WinnerHead board={board} />}
            {/* One spacing system, container-owned (#1828): gap between rows
                and pt from the header/bubbles are this list's; the step from
                the last row to the floating bar is SimpleScene's, the same
                10px token, correct at the last row by construction. */}
            {/* Clearance for the floating dock. It has to be IN FLOW: an
                absolutely positioned spacer anchored here starts at this
                container's padding box, and the sticky bottom section already
                sits ~70px below that inside the scroller, so it would only
                add the few pixels by which it out-reached the toggle — a
                reservation that measures right and reserves nothing.
                (SimpleScene's 1px pseudo-element CAN be absolute because it
                hangs off the SCROLLER itself, past all content.)

                What snapped the list was never that this is layout — it was
                that the amount CHANGED while you were at the bottom. Growth
                is harmless; only shrinking clamps scrollTop. So the value is
                measured once and kept (useDockInset), and gated on the board
                rather than on whether the dock is up. The dock's measured
                height carries the dock-to-toggle gap (its own mb-joy-step), not
                the row-to-dock one, so the step is added once on top: the
                last row clears the dock by the same token as every other
                pair on this surface. */}
            <div
                className="flex flex-col gap-joy-step pt-joy-step"
                style={dockInset > 0 ? {paddingBottom: dockInset} : undefined}
            >
                {rows.map((row) => (
                    <div
                        key={row.member.uuid}
                        ref={row.member.you ? rowRef : undefined}
                    >
                        {tab === "today" ? (
                            <TodayRow
                                member={row.member}
                                rank={row.rank}
                                goal={board.goal}
                                max={max}
                                liveBadges
                            />
                        ) : (
                            <WeekCard
                                member={row.member}
                                rank={row.rank}
                                goal={board.goal}
                                max={max}
                            />
                        )}
                    </div>
                ))}
            </div>
        </>
    );
};

const CLUB_TOP_STOP = "0.96 0.024 45";
const CLUB_TOP_STOP_DARK = "0.26 0.03 45";

const CLUB_GRADIENT = `linear-gradient(175deg, oklch(${CLUB_TOP_STOP}), oklch(0.958 0.020 60) 22%, oklch(0.958 0.020 320) 48%, oklch(0.94 0.03 278))`;

// The gradient's own top stop, translucent, so the header's scrolled-under
// state melts into the backdrop instead of going gray.
const CLUB_HEADER_SURFACE = `oklch(${CLUB_TOP_STOP} / 0.85)`;
// The light gradient's hue journey (warm peach into lilac), restated at dark
// lightness.
const CLUB_GRADIENT_DARK = `linear-gradient(175deg, oklch(${CLUB_TOP_STOP_DARK}), oklch(0.25 0.025 60) 22%, oklch(0.25 0.03 320) 48%, oklch(0.23 0.04 278))`;
const CLUB_HEADER_SURFACE_DARK = `oklch(${CLUB_TOP_STOP_DARK} / 0.85)`;

// The board rides SimpleScene: the gradient becomes a fixed backdrop behind the
// scene's own scroller (z-0, under the sticky header's z-20) and the content is
// plain in-flow, so the page scrolls — and bounces — as one.
//
// The gradient covers the whole window; the column width and the header band
// belong to SimpleScene now (the board's `scene` option), so this only paints
// the backdrop and stacks the content above it.
const BoardShell = ({children}: {children: React.ReactNode}) => (
    <>
        <div
            className="pointer-events-none fixed inset-0 z-0 dark:hidden"
            style={{background: CLUB_GRADIENT}}
        />
        <div
            className="pointer-events-none fixed inset-0 z-0 hidden dark:block"
            style={{background: CLUB_GRADIENT_DARK}}
        />
        <div
            className={classNames(
                "relative z-10 flex w-full flex-col px-1",
                CLUB_THEME,
            )}
        >
            {children}
        </div>
    </>
);

// The gate, demo and empty states are full-screen wizards, not the board: they
// keep the standalone shell (route shell stays EMPTY for them).
const Shell = ({
    children,
    menu,
}: {
    children: React.ReactNode;
    menu?: React.ReactNode;
}) => (
    <div className="fixed inset-0 flex flex-col" style={{background: CLUB_GRADIENT}}>
        <div className="pt-safe-or-padding pb-safe-or-padding relative mx-auto flex size-full max-w-md flex-col overflow-hidden px-5">
            <Logo menu={menu} />
            {children}
        </div>
    </div>
);

const useBoard = (initial: server.journal.clubs.WalkingClubBoard | null) => {
    const [board, setBoard] = React.useState(initial);
    const profile = useAppSelector(orm.getProfile);

    const refresh = React.useCallback(async () => {
        const result = await server.journal.clubs.walking_club_board({
            ignored: "",
        });
        if (result.type === "success" && result.data != null) {
            setBoard(result.data);
        }
    }, []);

    React.useEffect(() => {
        // No board at resolve means no membership; joining goes through
        // navigation and a fresh resolve, so there is nothing to poll for —
        // and a board resurrected here would disagree with the resolve-time
        // scene choice.
        if (initial == null) return;
        const interval = setInterval(() => void refresh(), 60_000);
        const listener = hardware.App.addListener("appStateChange", (state) => {
            if (state.isActive) {
                void (async () => {
                    // Re-reading the board alone shows stale data after hours
                    // in a pocket: push fresh HealthKit steps first, like the
                    // journal's steps card does on resume.
                    if (profile != null) {
                        await healthkit.checkAndSyncHealthData({
                            profile,
                            trace: "walking-club-resume",
                        });
                    }
                    await refresh();
                })();
            }
        });
        return () => {
            clearInterval(interval);
            void listener.then((handle) => handle.remove());
        };
    }, [initial, refresh, profile]);

    return {board, refresh};
};

// Past weeks are finished: fetch each offset once and keep it. load resolves
// true only when the board actually arrived, so callers can defer switching
// the view until there is something correct to show.
// One tagged state for week paging — the three shapes are mutually exclusive
// by construction, so "pending but already switched" and similar invalid
// combinations cannot be represented.
type WeekPaging =
    | {kind: "CURRENT"}
    | {kind: "PAST"; weeksAgo: number}
    | {kind: "LOADING"; from: number; target: number};

// Past weeks are finished, so each is fetched once — but they are cached by
// their ABSOLUTE Monday, never by relative offset: when the current week
// rolls over mid-session, offset 1 starts naming a different week, and an
// offset-keyed cache would serve the wrong one.
const usePastBoards = () => {
    const [boards, setBoards] = React.useState<Record<string, BoardData | undefined>>(
        {},
    );
    const load = React.useCallback(async (weeksAgo: number) => {
        const result = await server.journal.clubs.walking_club_board({
            ignored: "",
            weeks_ago: weeksAgo,
        });
        if (result.type !== "success" || result.data == null) {
            return false;
        }
        const data = fromServer(result.data);
        setBoards((previous) => ({...previous, [data.weekStart]: data}));
        return true;
    }, []);
    return {boards, load};
};

const demoState = (): {step: GateRoute | null; tab: BoardTab} => {
    if (typeof window === "undefined") {
        return {step: null, tab: "today"};
    }
    const params = new URLSearchParams(window.location.search);
    const step = params.get("step");
    return {
        step: DEMO_ROUTES.find((route) => route === step) ?? null,
        tab: params.get("tab") === "week" ? "week" : "today",
    };
};

const DemoContent = ({step}: {step: GateRoute}) => (
    <Shell>
        <GateScreen
            code="demo"
            clubName="Demo walk club"
            todayTotal={byToday(DEMO_BOARD)
                .filter((member) => !member.you)
                .reduce((sum, member) => sum + member.todayTotal, 0)}
            loggedInName={null}
            demo
            initialRoute={step}
            onDone={() => undefined}
        />
    </Shell>
);

const WalkingClubContent = (props: {
    demo: boolean;
    demoStep: GateRoute | null;
    board: BoardData | null;
    tab: BoardTab;
    rowRef: (element: HTMLElement | null) => void;
    dockInset: number;
}) => {
    const profile = useAppSelector(orm.getProfile);
    // Boxed users' whole app IS the club; anyone else needs a way out.
    const menu = !isWalkingClubBoxed(profile) ? <MenuButton /> : null;

    if (props.demo && props.demoStep != null) {
        return <DemoContent step={props.demoStep} />;
    }

    if (props.board == null) {
        return (
            <Shell menu={menu}>
                <div className="flex flex-1 flex-col items-center justify-center px-4 text-center">
                    <h3 className="mb-1.5 text-[27px] leading-[1.3] font-light">
                        No club yet
                    </h3>
                    <p className="text-[14px] leading-relaxed font-light text-stone-600">
                        Walking clubs are invite-only — ask your club for its join link.
                    </p>
                </div>
            </Shell>
        );
    }

    return (
        <BoardShell>
            <Board
                board={props.board}
                tab={props.tab}
                rowRef={props.rowRef}
                dockInset={props.dockInset}
            />
        </BoardShell>
    );
};

const WalkingClubJoinContent = (props: {
    code: string;
    info: server.journal.clubs.WalkingClubInfo | null;
    demo: boolean;
    profileName: string | null;
}) => {
    const [initial] = React.useState(demoState);
    const dispatch = useAppDispatch();
    const platform = useAppSelector((state) => state.boot.platform);
    // A magic link minted by walking_club_login_link carries ?sesame= back to
    // this page — consume it so the user lands signed in, mid-flow.
    const [sesame] = React.useState(() =>
        typeof window === "undefined"
            ? null
            : new URLSearchParams(window.location.search).get("sesame"),
    );
    const {info, demo} = props;
    // The resolve's viewer_name is authoritative on cold loads — the client
    // store has no profile until fast_init lands. The store covers the
    // just-signed-in-via-sesame case.
    const viewerName = info?.viewer_name ?? props.profileName;
    const [sesamePending, setSesamePending] = React.useState(
        () => sesame != null && info?.viewer_name == null && props.profileName == null,
    );

    React.useEffect(() => {
        if (!demo && info == null) {
            // SCENE_VIEW sees the route but not the dead-link state.
            void analytics.track("WALKING_CLUB_INVALID_LINK_VIEWED");
        }
    }, []);

    React.useEffect(() => {
        if (!sesamePending || sesame == null) {
            return;
        }
        void (async () => {
            const result = await server.core.rpc.validate_login_link({
                token: sesame,
            });
            if (result.type === "success") {
                await dispatch(auth.postLogin(result.data, true));
            }
            setSesamePending(false);
        })();
    }, []);

    if (demo && initial.step != null) {
        return <DemoContent step={initial.step} />;
    }

    if (sesamePending) {
        return (
            <Shell>
                <div className="flex-1" />
            </Shell>
        );
    }

    // The club needs Apple Health, so signup is iOS-only. Off production the
    // gate stays open (demo === non-production) so web testing still works.
    if (!demo && platform != null && platform !== "ios" && info != null) {
        return (
            <Shell>
                <div className="flex flex-1 flex-col items-center justify-center px-4 text-center">
                    <h3 className="mb-1.5 text-[27px] leading-[1.3] font-light">
                        Walking clubs live in the app
                    </h3>
                    <p className="text-[14px] leading-relaxed font-light text-stone-600">
                        {info.club_name} runs on your daily steps, so joining needs{" "}
                        <graphics.Logo className="inline h-[0.75em] w-auto overflow-visible align-baseline" />{" "}
                        on your iPhone. Get the app, then tap your invite link again.
                    </p>
                    <a
                        href={APP_STORE_URL}
                        onClick={() =>
                            void analytics.track("WALKING_CLUB_APP_STORE_CLICKED")
                        }
                        className="mt-6 block rounded-full bg-linear-to-br from-[#4c3ee0] to-[#6d5af0] px-8 py-[15px] text-[15.5px] font-semibold text-white"
                    >
                        Get the app
                    </a>
                </div>
            </Shell>
        );
    }

    if (info == null) {
        return (
            <Shell>
                <div className="flex flex-1 flex-col items-center justify-center px-4 text-center">
                    <h3 className="mb-1.5 text-[27px] leading-[1.3] font-light">
                        This link isn’t active
                    </h3>
                    <p className="text-[14px] leading-relaxed font-light text-stone-600">
                        Double-check the invite link your club shared, or ask for a
                        fresh one.
                    </p>
                </div>
            </Shell>
        );
    }

    return (
        <Shell>
            <GateScreen
                code={props.code}
                clubName={info.club_name}
                todayTotal={info.today_total / 1000}
                loggedInName={viewerName}
                demo={false}
                prefillsAllowed={props.demo}
                initialRoute={viewerName != null ? "profile" : "email"}
                onDone={() => void router.replace("walking_club.index", {})}
            />
        </Shell>
    );
};

// Boxed-in walking-only users (the WALKING_CLUB flag) land on the app root
// like everyone else; their whole app is the club, so send them there.
export const useWalkingClubBox = (): JournalImplementation => {
    React.useEffect(() => {
        void router.replace("walking_club.index", {});
    }, []);
    return {
        tabs: {
            journal: {
                view: <div />,
                title: "Home",
                icon: HomeIcon,
            },
            workout: null,
            summary: null,
            group: null,
            profile: null,
        },
        actions: [],
        options: {title: "Walking club", scene: {kind: "SIMPLE", nav: "none"}},
    };
};

// All board state lives here so the scene options and the view read ONE
// source: the resolve-time board only seeds it, and useBoard never resurrects
// a board the resolve did not have — chrome and content cannot disagree.
export const WalkingClub = router.WalkingClub.withHooks((props) => {
    const [initialDemo] = React.useState(demoState);
    const [tab, setTab] = React.useState<BoardTab>(initialDemo.tab);
    const {board} = useBoard(props.resolved.walking_club.board);
    const yourLocalSteps = useYourLocalSteps();
    const bottomBar = useBottomBarInset();
    const dock = useDockInset();
    const yourRow = useYourRowVisible(bottomBar.inset);
    const merged = React.useMemo(
        () =>
            board == null
                ? null
                : withYourLocalSteps(fromServer(board), yourLocalSteps),
        [board, yourLocalSteps],
    );
    const [paging, setPaging] = React.useState<WeekPaging>({kind: "CURRENT"});
    const pastBoards = usePastBoards();
    const pastBoardFor = (offset: number) =>
        merged == null
            ? undefined
            : pastBoards.boards[dates.weekStartFor(merged.weekStart, offset)];
    // What the view shows RIGHT NOW: while loading, still the week the user
    // was on.
    const weeksAgo = match(paging)
        .with({kind: "CURRENT"}, () => 0)
        .with({kind: "PAST"}, (state) => state.weeksAgo)
        .with({kind: "LOADING"}, (state) => state.from)
        .exhaustive();
    // The view only switches once the requested week's data is in hand, so a
    // paged board is never mislabeled current data. A past week only exists
    // on the week tab, so picking one lands there. A LOADING state's own
    // identity is the supersession guard: any navigation replaces it, and a
    // resolving fetch applies nothing unless its exact state is still
    // current.
    const requestWeek = (next: number) => {
        void analytics.track("WALKING_CLUB_WEEK_PAGED", {
            walking_club_weeks_ago: next,
        });
        if (next > 0) {
            setTab("week");
        }
        if (next === 0) {
            setPaging({kind: "CURRENT"});
            return;
        }
        if (pastBoardFor(next) != null) {
            setPaging({kind: "PAST", weeksAgo: next});
            return;
        }
        const loading: WeekPaging = {kind: "LOADING", from: weeksAgo, target: next};
        setPaging(loading);
        void pastBoards.load(next).then((loaded) => {
            setPaging((previous) =>
                previous !== loading
                    ? previous
                    : loaded
                      ? {kind: "PAST", weeksAgo: next}
                      : loading.from === 0
                        ? {kind: "CURRENT"}
                        : {kind: "PAST", weeksAgo: loading.from},
            );
        });
    };
    const displayedPast = weeksAgo === 0 ? null : pastBoardFor(weeksAgo);
    // The board actually on screen — what the dock, and the space held for
    // it, both have to reason about.
    const displayedBoard = displayedPast ?? merged;
    // A rollover while parked on history shifts every offset's meaning; the
    // cache (keyed by absolute Monday) simply no longer has this offset, and
    // the view falls back to the present rather than mislabeling.
    React.useEffect(() => {
        if (weeksAgo > 0 && displayedPast == null) {
            setPaging({kind: "CURRENT"});
        }
    }, [weeksAgo, displayedPast == null]);
    return {
        demo: props.resolved.walking_club.demo,
        demoStep: initialDemo.step,
        board: displayedBoard,
        tab,
        // Today only exists for the current week; selecting it leaves history
        // and, by replacing the paging state, strands any fetch in flight.
        setTab: (next: BoardTab) => {
            setTab(next);
            if (next === "today") {
                setPaging({kind: "CURRENT"});
            }
        },
        weeksAgo,
        weeksAvailable: merged?.weeksAvailable ?? 0,
        currentWeekStart: merged?.weekStart ?? null,
        requestWeek,
        pagePending: paging.kind === "LOADING",
        rowRef: yourRow.setElement,
        toggleRef: bottomBar.setElement,
        dockRef: dock.setElement,
        // The reservation is a property of the BOARD, never of the scroll
        // position. Gating it on whether the dock is UP is what snapped the
        // list; gating it on whether this board can dock AT ALL is free —
        // paging weeks already swaps the whole list, so the extent was going
        // to change anyway. A past week can never dock (showDock below), so it
        // must not carry the clearance and blow the 10px end-spacing token.
        // The measurement itself is still kept: re-measuring on the way back
        // to the current week would reintroduce the mid-scroll growth.
        // Same predicate the dock renders from, never a copy of it — plus
        // the paging concern, which is this level's to know.
        dockInset:
            weeksAgo === 0 &&
            displayedBoard != null &&
            dockRow(displayedBoard, tab) != null
                ? dock.inset
                : 0,
        showDock: yourRow.isVisible === false && weeksAgo === 0,
        // Boxed users' whole app IS the club, so they get no way out of it.
        isBoxed: isWalkingClubBoxed(props.profile),
    };
})
    .withIndex((props) => (
        <WalkingClubContent
            demo={props.demo}
            demoStep={props.demoStep}
            board={props.board}
            tab={props.tab}
            rowRef={props.rowRef}
            dockInset={props.dockInset}
        />
    ))
    .withActions(() => [])
    // The board rides SimpleScene, which is what gives it the scene scroller
    // (so it bounces), the shrinking translucent header, the pinned bottom bar
    // and notifications that stack above it. The gate, demo and empty states
    // are full-screen wizards and keep their own shell — SIMPLE chrome would
    // stack a second header over them.
    .withOptions((props) =>
        (props.demo && props.demoStep != null) || props.board == null
            ? {title: "Walking club", scene: {kind: "EMPTY"}}
            : {
                  title: "Walking club",
                  respectDarkMode: true,
                  titleSection: <ClubTitle />,
                  bottomSection: (
                      <BoardBottom
                          board={props.board}
                          tab={props.tab}
                          setTab={props.setTab}
                          weeksAgo={props.weeksAgo}
                          weeksAvailable={props.weeksAvailable}
                          currentWeekStart={props.currentWeekStart}
                          requestWeek={props.requestWeek}
                          pagePending={props.pagePending}
                          showDock={props.showDock}
                          toggleRef={props.toggleRef}
                          dockRef={props.dockRef}
                      />
                  ),
                  scene: {
                      kind: "SIMPLE",
                      nav: props.isBoxed ? "none" : "menu",
                      // The phone-width column, kept on desktop too; the
                      // header band still runs the full window over the
                      // gradient.
                      width: "column",
                      surface: {
                          kind: "custom",
                          background: CLUB_HEADER_SURFACE,
                          darkBackground: CLUB_HEADER_SURFACE_DARK,
                      },
                  },
              },
    );

export const WalkingClubJoin = router.WalkingClubJoin.withHooks((props) => ({
    code: props.params.code,
    info: props.resolved.walking_club_join.info,
    demo: props.resolved.walking_club_join.demo,
    profileName: props.profile?.name ?? null,
}))
    .withIndex((props) => (
        <WalkingClubJoinContent
            code={props.code}
            info={props.info}
            demo={props.demo}
            profileName={props.profileName}
        />
    ))
    .withActions(() => [])
    .withOptions(() => ({title: "Walking club", scene: {kind: "EMPTY"}}));
